/**
 * Copyright (c) 2023, CYBRA Corp. All rights reserved.
 */
import { Component, OnInit, Input, ViewChild } from "@angular/core";

import { ActionInstance } from "app/models/action-instance";
import { Event } from "app/models/event";
import { ObjectService } from "app/services/object.service";
import { ServiceService } from "app/services/service.service";
import { ReaderService } from "app/services/reader.service";
import { BaseInstance } from "app/models/base-instance";
import { Function } from "app/models/function";
import { ObjectInstance } from "app/models/object-instance";
import { ReaderInstance } from "app/models/reader-instance";
import { ServiceInstance } from "app/models/service-instance";
import { FunctionParameter } from "app/models/function-parameter";
import { PropertyType } from "app/models/property-type";
import { EntityType } from "app/models/entity-type";
import { InstanceData } from "app/models/instance-data";
import { ERROR_DIALOG_TITLE, SimpleSuccessErrorDialogComponent } from "app/components/simple-success-error-dialog-box/simple-success-error-dialog-box.component";

@Component({
  selector: 'app-simple-function-action-editor',
  templateUrl: './simple-function-action-editor.component.html'
})
export class SimpleFunctionActionEditorComponent implements OnInit {

  /** Reference to the SimpleSuccessErrorDialog used to display error */
  @ViewChild('successErrorDialog') 
  simpleSuccessErrorDialogComponent: SimpleSuccessErrorDialogComponent;

  //Store a reference to the enum in the component so that it can be used in the html file [ngSwitch]
  propertyType = PropertyType;
  
  action: ActionInstance;

  @Input()
  set mAction(action: ActionInstance) {
    console.log("Setting Simple Function Action's Action to: " + (this.action == null? "*NULL*" : (this.action.id + ":" + this.action.name + " [" + (this.action.action == null? "*NULL*" : (this.action.action.id + ":" + this.action.action.name)  + "]"))));
    this.action = action;

    //1) Create the 'data' Action Data if needed (Will be for new Actions)
    if (this.action.data == null) {
      this.action.data = [];
    }
    if (this.action.data.find(d => d.property.property == 'data') == null) {
      console.log("Creating Simple Function Action's 'data' Data value...");
      const data = new InstanceData();
      data.property = this.mAction.action.properties.find(property => property.property == 'data');
      this.action.data.push(data);
    }

    //2) Load the values from the Action 
    this.loadFromData();
  }
  get mAction(): ActionInstance {
    return this.action;
  }
  
  @Input()
  mEvent: Event;  

  @Input()
  mUniqueIndex: number;

  /*
   * Step 1: Select Object Type
   */
  mAvailableObjectTypes = [ObjectTypeEnum.Device, ObjectTypeEnum.Reader, ObjectTypeEnum.Service];
  mObjectType: ObjectTypeEnum = null;

  /*
   * Step 2: Select Instance
   */
  mInstance: BaseInstance = null;
  mAvailableInstances: BaseInstance[] = [];

  /*
   * Step 3: Select Function
   */
  mFunction: Function = null;
  mAvailableFunctions: Function[] = [];

  /*
   * Step 4: Specify Parameters
   */
  mParameterValues: ParameterData[] = [];



  constructor(
    private mObjectService: ObjectService,
    private mReaderService: ReaderService,
    private mServiceService: ServiceService
  ) { }


  ngOnInit(): void {
    console.log("Initializing Simple Function Action Editor..." + 
      "\n\tAction: " + (this.mAction == null? "*NULL*" : (this.mAction.id + ":" + this.mAction.name + " [" + (this.mAction.action == null? "*NULL*" : (this.mAction.action.id + ":" + this.mAction.action.name)  + "]"))) + 
      "\n\tEvent: " + (this.mEvent == null? "*NULL*" : (this.mEvent.id + ":" + this.mEvent.name)) +  
      "\n\tUnique Index: '" + this.mUniqueIndex + "'"
    );
  }

  /*
   * Loads the UI values based on what is currently stored in the Action's Data
   */
  async loadFromData(): Promise<void> {
    console.log("loadFromData()");

    //Reset the current UI values to the defaults / blank
    this.mObjectType = null;
    this.mInstance = null;
    this.mAvailableInstances = [];
    this.mFunction = null;
    this.mAvailableFunctions = [];
    this.mParameterValues = [];

    //Load from the Action Data if available
    if (this.mAction != null && this.mAction.data != null) {
      const instanceData = this.mAction.data.find(d => d.property.property == 'data');
      if (instanceData != null && instanceData.value != null) {
        try {
          const functionData = JSON.parse(instanceData.value) as SimpleFunctionActionData;
          if (functionData.entity_type != null) {
            this.mObjectType = this._getObjectType(functionData.entity_type);
            if (this.mObjectType != null) {
              await this.refreshAvailableInstances();
              if (functionData.instance_id != null) {
                this.mInstance = this.mAvailableInstances.find(i => i.id == functionData.instance_id);
                if (this.mInstance != null) {
                  await this.refreshAvailableFunctions();
                  if (functionData.function_key != null) {
                    this.mFunction = this.mAvailableFunctions.find(f => f.key == functionData.function_key);
                    if (this.mFunction != null) {
                      await this.refreshParameterValues();
                      if (functionData.parameter_values != null) {
                        this.mParameterValues.forEach(parameterData => {
                          const parameterKey = parameterData.mParameter.key;
                          if (parameterKey != null) {
                            const values = functionData.parameter_values[parameterKey];
                            if (values != null && values.length > 0) {
                              parameterData.mParameterValues = [];
                              values.forEach(value => {
                                this.addValueToParameterData(value, parameterData);
                              });
                            }
                          }
                        });
                      }
                    }
                  }
                }
              }
            }
          }
        } catch (error) {
          this.handleError(error);
        }
      }
    }
  }

  updateActionInstance(): void {
    console.log("updateActionInstance()");

    const dto = new SimpleFunctionActionData();
    dto.entity_type = this._getEntityType();
    dto.instance_id = this.mInstance == null? null : this.mInstance.id;
    dto.function_key = this.mFunction == null? null : this.mFunction.key;
    dto.parameter_values = {};
    if (this.mParameterValues != null) {
      this.mParameterValues.forEach(parameter => {
        const key = parameter.mParameter.key;
        const values: string[] = [];
        if (parameter.mParameterValues != null) {
          parameter.mParameterValues.forEach(parameterValue => {
            if (parameterValue.mValue != null) {
              values.push(parameterValue.mValue);
            }
          });
        }
        if (values.length > 0) {
          dto.parameter_values[key] = values;
        }
      });
    }

    const json = JSON.stringify(dto);
    console.log("Updating Action's Data to: " + json);

    const datum = this.mAction.data.find(d => d.property.property == 'data');
    datum.value = json;
    datum.object_value = json;
  }

  private _getEntityType(): EntityType {
    switch (this.mObjectType) {
      case ObjectTypeEnum.Device: {
        return EntityType.OBJECT;
      }
      case ObjectTypeEnum.Reader: {
        return EntityType.READER;
      }
      case ObjectTypeEnum.Service: {
        return EntityType.SERVICE;
      }
      default: {
        return null;
      }
    }
  }

  private _getObjectType(entityType: EntityType): ObjectTypeEnum {
    switch (entityType) {
      case EntityType.OBJECT: {
        return ObjectTypeEnum.Device;
      }
      case EntityType.READER: {
        return ObjectTypeEnum.Reader;
      }
      case EntityType.SERVICE: {
        return ObjectTypeEnum.Service;
      }
      default: {
        return null;
      }
    }
  }





  /*
   * "Step 1) Select Object Type" methods
   */
  onSelectedObjectTypeChanged(): void {
    console.log("Selected Object Type Changed! Value: '" + this.mObjectType + "'");
    this.mInstance = null;
    this.refreshAvailableInstances();
    this.updateActionInstance();
  }

  async refreshAvailableInstances(): Promise<void> {
    this.mAvailableInstances = [];
    if (this.mObjectType != null) {
      try {
        switch (this.mObjectType) {
          case ObjectTypeEnum.Device: {
            const devices = await this.mObjectService.getAllDeviceObjectInstances();
            if (devices != null) {
              this.mAvailableInstances.push(...devices);
            }
            break;
          }
          case ObjectTypeEnum.Reader: {
            const readers = await this.mReaderService.getAllReaderInstances();
            if (readers != null) {
              this.mAvailableInstances.push(...readers);
            }
            break;
          }
          case ObjectTypeEnum.Service: {
            const services = await this.mServiceService.getAllServiceInstances();
            if (services != null) {
              this.mAvailableInstances.push(...services);
            }
            break;
          }
          default: {
            console.warn("Unknown Object Type encountered while obtaining Available Instances.  Object Type: '" + this.mObjectType + "'!");
            break;
          }
        }
      } catch (error) {
        this.handleError(error);
      }
    }
  }





  /*
   * "Step 1) Select instance" methods
   */
  onSelectedInstanceChanged(): void {
    console.log("Selected Instance Changed! Value: '" + (this.mInstance == null? "*NULL*" : (this.mInstance.id + "/" + this.mInstance.name)) + "'");
    this.mFunction = null;
    this.refreshAvailableFunctions();
    this.updateActionInstance();
  }

  async refreshAvailableFunctions(): Promise<void> {
    this.mAvailableFunctions = [];
    if (this.mObjectType != null && this.mInstance != null) {
      try {
        switch (this.mObjectType) {
          case ObjectTypeEnum.Device: {
            const device = <ObjectInstance> this.mInstance;
            if (device.object != null) {
              const type = await this.mObjectService.getObjectForId(device.object.id);
              if (type.functions != null) {
                this.mAvailableFunctions.push(...type.functions);
              }
            }
            break;
          }
          case ObjectTypeEnum.Reader: {
            const reader = <ReaderInstance> this.mInstance;
            if (reader.reader != null) {
              const type = await this.mReaderService.getReaderById(reader.reader.id);
              if (type.functions != null) {
                this.mAvailableFunctions.push(...type.functions);
              }
            }
            break;
          }
          case ObjectTypeEnum.Service: {
            const service = <ServiceInstance> this.mInstance;
            if (service.service != null) {
              const type = await this.mServiceService.getServiceById(service.service.id);
              if (type.functions != null) {
                this.mAvailableFunctions.push(...type.functions);
              }
            }
            break;
          }
          default: {
            console.warn("Unknown Object Type encountered while obtaining Available Functions.  Object Type: '" + this.mObjectType + "'!");
            break;
          }
        }
      } catch (error) {
        this.handleError(error);
      }
    }
  }





  /*
   * "Step 3) Select Function" methods
   */
  onSelectedFunctionChanged(): void {
    console.log("Selected Function Changed!  Value: '" + (this.mFunction == null? "*NULL*" : (this.mFunction.id + "/" + this.mFunction.key + "/" + this.mFunction.name)) + "'");
    this.refreshParameterValues();
    this.updateActionInstance();
  }





  /*
   * "Step 4) Specify Parameter" methods
   */
  async refreshParameterValues(): Promise<void> {
    this.mParameterValues = [];
    if (this.mFunction != null && this.mFunction.parameters != null && this.mFunction.parameters.length > 0) {
      this.mFunction.parameters.forEach(parameter => {
        const data = new ParameterData();
        data.mParameter = parameter;
        this.addValueToParameterData(null, data);
        this.mParameterValues.push(data);
      });
    }
  }

  addValueToParameterData(value: string, parameterData: ParameterData): void {
    const parameterValue = new ParameterValue();
    parameterValue.mValue = value;
    parameterData.mParameterValues.push(parameterValue);
  }

  removeValueFromParameterData(parameterValue: ParameterValue, parameterData: ParameterData): void {
    if (parameterData.mParameterValues.length == 1) {
      if (parameterValue.mValue != null) {
        parameterValue.mValue = null;
        this.updateActionInstance();
      }
    } else {
      const index = parameterData.mParameterValues.indexOf(parameterValue);
      if (index > -1) {
        parameterData.mParameterValues.splice(index, 1);
        this.updateActionInstance();
      }
    }
  }

  onParameterDataChange(parameterData: ParameterData, parameterValue: ParameterValue): void {
    console.log("onParamterDataChange() - Parameter Data: " + (parameterData == null? "*NULL*" : ("'" + parameterData.mParameter.key + "'")) + ", Parameter Value: " + (parameterValue == null? "*NULL*" : (parameterValue.mValue == null? "*NULL-VALUE" : ("'" + parameterValue.mValue + "'"))));
    this.updateActionInstance();
  }



  
  handleError(error: any): void {
    console.error("handleError() - Error: " + error);
    if (error != null) {
      const response: Response = error;
      this.simpleSuccessErrorDialogComponent.showErrorDialog(ERROR_DIALOG_TITLE, response.text().toString());
    }
  }

}

enum ObjectTypeEnum {
  Device = 'Device',
  Reader = 'Reader',
  Service = 'Service'
}

class SimpleFunctionActionData {
  entity_type: EntityType;
  instance_id: number;
  function_key: string;
  parameter_values: {[propKey: string]: string[]};
}

export class ParameterData {
  mParameter: FunctionParameter;
  mParameterValues: ParameterValue[] = [];
}

export class ParameterValue {
  mValue:string;
}
